import React, { useCallback, useEffect, useState } from "react";
import { assignPartner, listPartnerAssignments, listPartners, mentorRoleForCourseType, partnerRoleLabel, removePartnerAssignment, type PartnerAssignment, type PartnerProfile } from "../../Lib/partnerApi";

export default function CourseCoordinatorAssignments({ courseRunId, courseType, locked = false }: { courseRunId: string; courseType: string; locked?: boolean }) {
    const [partners, setPartners] = useState<PartnerProfile[]>([]);
    const [assignments, setAssignments] = useState<PartnerAssignment[]>([]);
    const [partnerId, setPartnerId] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setBusy(true); setError("");
        try {
            const [allPartners, current] = await Promise.all([listPartners(), listPartnerAssignments("COURSE_RUN", courseRunId)]);
            setPartners(allPartners);
            setAssignments(current);
        } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not load coordinators."); }
        finally { setBusy(false); }
    }, [courseRunId]);

    useEffect(() => { void load(); }, [load]);

    async function add() {
        if (!partnerId) return;
        setBusy(true); setError("");
        try { await assignPartner({ partnerId, resourceType: "COURSE_RUN", resourceId: courseRunId, assignmentRole: "COORDINATOR" }); setPartnerId(""); await load(); }
        catch (cause) { setError(cause instanceof Error ? cause.message : "Could not assign coordinator."); setBusy(false); }
    }

    async function remove(assignment: PartnerAssignment) {
        setBusy(true); setError("");
        try { await removePartnerAssignment({ partnerId: assignment.partnerId, assignmentId: assignment.id }); await load(); }
        catch (cause) { setError(cause instanceof Error ? cause.message : "Could not remove coordinator."); setBusy(false); }
    }

    const requiredRole = mentorRoleForCourseType(courseType);
    const eligiblePartners = requiredRole ? partners.filter((partner) => partner.status === "ACTIVE" && partner.roles.includes(requiredRole)) : [];
    const name = (id: string) => partners.find((partner) => partner.id === id)?.name || "Mentor";
    return <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
        <h4 className="font-black text-[#061a4a]">Program mentors</h4>
        <p className="mt-1 text-xs text-slate-600">{locked ? "Mentor history is read-only for this run." : requiredRole ? `Only active ${partnerRoleLabel(requiredRole).toLowerCase()}s are eligible for this ${courseType.toLowerCase()} run.` : "Mentor assignment is available only for internship and workshop runs."}</p>
        {!locked && requiredRole && <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <select value={partnerId} onChange={(event) => setPartnerId(event.target.value)} disabled={busy} className="min-w-0 flex-1 rounded-xl border border-blue-200 bg-white px-3 py-2 text-sm">
                <option value="">{eligiblePartners.length ? `Select an active ${partnerRoleLabel(requiredRole).toLowerCase()}` : `No active ${partnerRoleLabel(requiredRole).toLowerCase()} available`}</option>
                {eligiblePartners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} · {partnerRoleLabel(requiredRole)}</option>)}
            </select>
            <button type="button" disabled={busy || !partnerId} onClick={add} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Assign mentor</button>
        </div>}
        <div className="mt-3 flex flex-wrap gap-2">{assignments.filter((assignment) => assignment.assignmentRole === "COORDINATOR").map((assignment) => <span key={assignment.id} className="rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-bold text-slate-700">{name(assignment.partnerId)} {!locked && <button type="button" disabled={busy} onClick={() => remove(assignment)} className="ml-2 text-red-600">Remove</button>}</span>)}</div>
        {error && <p className="mt-2 text-xs font-bold text-red-700">{error}</p>}
    </div>;
}
